import FontAwesome from '@expo/vector-icons/FontAwesome'
import { useIsFocused } from '@react-navigation/native'
import { Link, Stack, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native'
import Footer from '@/components/Footer'
import PublicHeader from '@/components/PublicHeader'
import { Text } from '@/components/ui/Text'
import { useTheme } from '@/contexts/ThemeContext'
import { serializeJsonForScriptTag } from '@/lib/jsonForScriptTag'
const landingVideo = require('@/assets/videos/landing.mp4')

type LandingStat = {
  iconName: ComponentProps<typeof FontAwesome>['name']
  label: string
}

type NavigatorWithConnection = Navigator & {
  connection?: {
    saveData?: boolean
  }
}

function getStats(t: (key: string) => string): LandingStat[] {
  return [
    { iconName: 'map-marker', label: t('index.statsGoal') },
    { iconName: 'usd', label: t('index.statsDate') },
    { iconName: 'comments', label: t('index.statsInvolved') },
  ]
}

function getVideoFallbackDelay(saveData: boolean | undefined, isSmallViewport: boolean): number {
  if (saveData) {
    return isSmallViewport ? 2800 : 2600
  }
  return isSmallViewport ? 1400 : 1000
}

function useVideoAutoLoad(isSmallViewport: boolean): {
  shouldLoadVideo: boolean
  videoRef: React.MutableRefObject<HTMLVideoElement | null>
  isVideoReady: boolean
  setIsVideoReady: (v: boolean) => void
} {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined') return
    if (shouldLoadVideo) return

    const navigatorWithConnection = navigator as NavigatorWithConnection
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const shouldLimitAutoLoad = navigatorWithConnection.connection?.saveData === true
    const enableVideo = () => setShouldLoadVideo(true)

    const fallbackTimer = setTimeout(
      enableVideo,
      getVideoFallbackDelay(shouldLimitAutoLoad, isSmallViewport)
    )

    window.addEventListener('pointerdown', enableVideo, { once: true, passive: true })
    window.addEventListener('wheel', enableVideo, { once: true, passive: true })
    window.addEventListener('touchstart', enableVideo, { once: true, passive: true })
    window.addEventListener('keydown', enableVideo, { once: true })

    return () => {
      window.removeEventListener('pointerdown', enableVideo)
      window.removeEventListener('wheel', enableVideo)
      window.removeEventListener('touchstart', enableVideo)
      window.removeEventListener('keydown', enableVideo)
      clearTimeout(fallbackTimer)
    }
  }, [isSmallViewport, shouldLoadVideo])

  return { shouldLoadVideo, videoRef, isVideoReady, setIsVideoReady }
}

function useVideoPlayback(
  shouldLoadVideo: boolean,
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  isFocused: boolean
) {
  useEffect(() => {
    if (!shouldLoadVideo) return
    const element = videoRef.current
    if (!element) return

    if (!isFocused) {
      element.pause()
      return
    }

    void element.play().catch(() => {})
  }, [isFocused, shouldLoadVideo, videoRef])

  useEffect(() => {
    if (!shouldLoadVideo) return
    const element = videoRef.current

    return () => {
      element?.pause()
    }
  }, [shouldLoadVideo, videoRef])
}

function useLandingThemeColors(isDark: boolean, colors: ReturnType<typeof useTheme>['colors']) {
  return {
    overlayColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
    headlineColor: isDark ? '#FFFFFF' : colors.dark,
    statsBg: isDark ? 'rgba(0,0,0,0.60)' : 'rgba(255,255,255,0.72)',
    statsBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(26,26,26,0.1)',
    statValueColor: isDark ? '#FFFFFF' : colors.dark,
    statLabelColor: isDark ? 'rgba(255,255,255,0.7)' : colors.muted,
    heroFallbackBg: isDark ? '#10141b' : '#e8ded1',
    heroBadgeBg: isDark ? 'rgba(232,115,42,0.15)' : 'rgba(255,255,255,0.72)',
    textPrimary: isDark ? '#FFFFFF' : '#000000',
  }
}

export default function Index() {
  const router = useRouter()
  const { t } = useTranslation()
  const { colors, isDark } = useTheme()
  const { width: viewportWidth } = useWindowDimensions()
  const isFocused = useIsFocused()
  const STATS = getStats(t)
  const [scrollOffset, setScrollOffset] = useState(0)
  const isSmallViewport = viewportWidth < 900

  const {
    shouldLoadVideo,
    videoRef: landingVideoRef,
    isVideoReady,
    setIsVideoReady,
  } = useVideoAutoLoad(isSmallViewport)

  useVideoPlayback(shouldLoadVideo, landingVideoRef, isFocused)

  const themeColors = useLandingThemeColors(isDark, colors)
  const webCriticalFontFamily =
    Platform.OS === 'web'
      ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
      : undefined

  return (
    <View style={{ flex: 1, width: '100%', height: '100%' }}>
      <Head>
        <title>{t('index.pageTitle')}</title>
        <meta name="description" content={t('index.pageDescription')} />
        <link rel="canonical" href="https://turbinago.com/" />
        <meta property="og:title" content={t('index.pageTitle')} />
        <meta property="og:description" content={t('index.pageDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://turbinago.com/" />
        <meta property="og:image" content="https://turbinago.com/og-default.png" />
        <meta property="og:site_name" content="Turbina" />
        <meta property="og:locale" content="es_419" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('index.pageTitle')} />
        <meta name="twitter:description" content={t('index.pageDescription')} />
        <meta name="twitter:image" content="https://turbinago.com/og-default.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonForScriptTag({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Turbina',
              url: 'https://turbinago.com',
              logo: 'https://turbinago.com/favicon.png',
              description: 'Platform helping young Latin Americans plan international experiences',
              sameAs: [
                'https://www.instagram.com/otrosmeridianos/',
                'https://www.youtube.com/@otrosmeridianos',
              ],
            }),
          }}
        />
      </Head>

      <View
        className="absolute inset-0"
        style={{ backgroundColor: themeColors.heroFallbackBg, zIndex: 0 }}
      />

      {Platform.OS === 'web' && shouldLoadVideo && (
        <video
          ref={landingVideoRef}
          src={landingVideo}
          autoPlay
          muted
          playsInline
          preload="metadata"
          poster="/og-default.png"
          aria-hidden="true"
          onCanPlay={() => setIsVideoReady(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            opacity: isVideoReady ? 1 : 0,
            transition: 'opacity 240ms ease-out',
          }}
        />
      )}

      <View
        className="absolute inset-0"
        style={{ backgroundColor: themeColors.overlayColor, zIndex: 1 }}
      />

      <Stack.Screen
        options={{
          title: t('index.pageTitle'),
          contentStyle: { backgroundColor: 'transparent' },
        }}
      />

      <View style={{ flex: 1, zIndex: 2 }}>
        <PublicHeader activeRoute="/" transparent scrollOffset={scrollOffset} />

        <ScrollView
          onScroll={(e) => setScrollOffset(e.nativeEvent.contentOffset.y)}
          scrollEventThrottle={16}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: 30,
          }}
          className="flex-1"
        >
          <View className="flex-1 items-center justify-center px-4 gap-8 p-10 lg:gap-16 xl:gap-20 2xl:gap-24 pt-0">
            <View
              className="px-4 py-2 rounded-full border"
              style={{
                borderColor: colors.primary,
                backgroundColor: themeColors.heroBadgeBg,
              }}
            >
              <Text
                className="text-xs font-medium tracking-widest uppercase"
                style={{
                  color: themeColors.textPrimary,
                  fontFamily: webCriticalFontFamily,
                }}
              >
                {t('index.heroBadge')}
              </Text>
            </View>

            {Platform.OS === 'web' ? (
              <View className="items-center">
                <h1
                  className="text-2xl md:text-5xl font-medium text-center font-sans"
                  style={{
                    color: themeColors.headlineColor,
                    letterSpacing: -1,
                    fontFamily: webCriticalFontFamily,
                  }}
                >
                  {t('index.title')}
                </h1>
                <Link href="/about#video">
                  <View className="flex-row items-center gap-1.5 mt-3">
                    <FontAwesome name="external-link" size={12} color={colors.primary} />
                    <Text
                      className="text-sm font-semibold tracking-wider uppercase"
                      style={{
                        color: colors.primary,
                        fontFamily: webCriticalFontFamily,
                      }}
                    >
                      {t('index.pilotCohort')}
                    </Text>
                  </View>
                </Link>
              </View>
            ) : (
              <View className="items-center">
                <Text
                  className="text-2xl md:text-5xl font-medium text-center"
                  style={{ color: themeColors.headlineColor, letterSpacing: -1 }}
                >
                  {t('index.title')}
                </Text>
                <Link href="/about#video">
                  <View className="flex-row items-center gap-1.5 mt-3">
                    <FontAwesome name="external-link" size={12} color={colors.primary} />
                    <Text
                      className="text-sm font-semibold tracking-wider uppercase"
                      style={{ color: colors.primary }}
                    >
                      {t('index.pilotCohort')}
                    </Text>
                  </View>
                </Link>
              </View>
            )}

            <View className="items-center gap-3 mt-2">
              <Pressable
                className="px-12 py-4 rounded-full"
                style={{ backgroundColor: colors.primary }}
                onPress={() => router.push('/onboarding')}
              >
                <Text
                  className="text-sm font-medium tracking-widest uppercase"
                  style={{ color: '#fff', fontFamily: webCriticalFontFamily }}
                >
                  {t('index.cta')}
                </Text>
              </Pressable>
              <Text
                className="text-sm font-medium"
                style={{ color: themeColors.textPrimary, fontFamily: webCriticalFontFamily }}
              >
                {t('index.secondaryText')}
              </Text>
            </View>
            <View
              className="mt-6 flex-row rounded-2xl overflow-hidden"
              style={{
                backgroundColor: themeColors.statsBg,
                borderWidth: 1,
                borderColor: themeColors.statsBorder,
              }}
            >
              {STATS.map((stat, i) => (
                <View
                  key={stat.label}
                  className="items-center py-4 px-8 sm:px-16 flex-1"
                  style={{
                    borderRightWidth: i < STATS.length - 1 ? 1 : 0,
                    borderRightColor: themeColors.statsBorder,
                  }}
                >
                  <FontAwesome name={stat.iconName} size={15} color={themeColors.statValueColor} />
                  <Text
                    className="sm:text-xs text-[10px] tracking-wide uppercase mt-0.5 sm:whitespace-nowrap pt-2 text-center"
                    style={{ color: themeColors.statLabelColor, fontFamily: webCriticalFontFamily }}
                  >
                    {stat.label}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
        <Footer floating showTermsLink />
      </View>
    </View>
  )
}
